import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById } from '@/lib/api/processors'
import { useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'
import { SecretInput } from '@/components/form/secret-input'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { CheckoutEditSkeleton } from '../components/checkout-edit-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type AuthType = 'apiKey' | 'clientCredentials'

const CheckoutEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id!)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [authType, setAuthType] = useState<AuthType>('clientCredentials')
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        sandboxed: false,
        clientId: '',
        clientSecret: '',
        apiKey: '',
        dryMode: false,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            const hasApiKey = options?.apiKey !== undefined

            setAuthType(hasApiKey ? 'apiKey' : 'clientCredentials')
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                sandboxed: options?.sandboxed ?? false,
                clientId: options?.clientId || '',
                clientSecret: options?.clientSecret || '',
                apiKey: options?.apiKey || '',
                dryMode: options?.dryMode === true,
            })
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (authType === 'apiKey' && !formData.apiKey.trim()) {
            setValidationError(t('apiKeyRequired'))
            return
        }

        if (authType === 'clientCredentials') {
            if (!formData.clientId.trim()) {
                setValidationError(t('clientIdRequired'))
                return
            }
            if (!formData.clientSecret.trim()) {
                setValidationError(t('clientSecretRequired'))
                return
            }
        }

        setValidationError(null)

        try {
            const options = authType === 'apiKey'
                ? {
                    apiKey: formData.apiKey,
                    sandboxed: formData.sandboxed,
                    dryMode: formData.dryMode,
                }
                : {
                    clientId: formData.clientId,
                    clientSecret: formData.clientSecret,
                    sandboxed: formData.sandboxed,
                    dryMode: formData.dryMode,
                }

            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    type: processor!.type,
                    name: formData.name,
                    enabled: formData.enabled,
                    options,
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(-1)
    }

    if (isLoading) {
        return (
            <div className="container mx-auto py-8">
                <CheckoutEditSkeleton />
            </div>
        )
    }

    if (!processor || processor.type !== 'checkout') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('editCheckoutConfiguration')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('checkoutEnvironment')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="sandboxed"
                                checked={formData.sandboxed}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, sandboxed: checked === true })
                                }
                            />
                            <Label htmlFor="sandboxed">{t('enableSandboxMode')}</Label>
                        </div>

                        {formData.sandboxed && (
                            <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                                <p className="text-sm text-yellow-700">
                                    ⚠️ {t('checkoutTestModeWarning')}
                                </p>
                                <a
                                    href="https://www.checkout.com/docs/developer-resources/testing/test-cards"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block text-sm text-blue-600 underline hover:text-blue-800"
                                >
                                    {t('viewCheckoutTestCards')} →
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('authenticationMethod')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={authType} onValueChange={(value) => setAuthType(value as AuthType)}>
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="clientCredentials">{t('clientCredentialsAuth')}</TabsTrigger>
                                <TabsTrigger value="apiKey">{t('apiKeyAuth')}</TabsTrigger>
                            </TabsList>

                            <TabsContent value="clientCredentials" className="space-y-4 mt-4">
                                <div>
                                    <Label htmlFor="clientId">{t('clientId')} *</Label>
                                    <Input
                                        id="clientId"
                                        value={formData.clientId}
                                        onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                                        placeholder={t('clientIdPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <SecretInput
                                        label={`${t('clientSecret')} *`}
                                        value={formData.clientSecret}
                                        onChange={(value) => setFormData({ ...formData, clientSecret: value })}
                                        placeholder={t('clientSecretPlaceholder')}
                                    />
                                </div>

                                <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                                    <p className="text-sm text-blue-700">
                                        <strong>{t('howToGetCredentials')}:</strong>
                                    </p>
                                    <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                        <li>{t('checkoutLoginToDashboard')}</li>
                                        <li>{t('checkoutNavigateToSettings')}</li>
                                        <li>{t('checkoutCreateOrCopyCredentials')}</li>
                                        <li>{t('checkoutUseCorrectEnvironment')}</li>
                                    </ol>
                                </div>
                            </TabsContent>

                            <TabsContent value="apiKey" className="space-y-4 mt-4">
                                <div>
                                    <SecretInput
                                        label={`${t('apiKey')} *`}
                                        value={formData.apiKey}
                                        onChange={(value) => setFormData({ ...formData, apiKey: value })}
                                        placeholder={t('apiKeyPlaceholder')}
                                    />
                                </div>

                                <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                                    <p className="text-sm text-blue-700">
                                        <strong>{t('howToGetApiKey')}:</strong>
                                    </p>
                                    <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                        <li>{t('checkoutLoginToDashboard')}</li>
                                        <li>{t('checkoutNavigateToSettings')}</li>
                                        <li>{t('checkoutCreateOrCopyApiKey')}</li>
                                    </ol>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const CheckoutEditPage = withRequiredPermission(CheckoutEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)

